import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowLeft, ArrowUp, ChevronRight, ChevronsUpDown } from 'lucide-react'
import { publicAPI } from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import ErrorMessage from '../../components/ErrorMessage'
import SearchBar from '../../components/SearchBar'
import ExportButtons from '../../components/ExportButtons'
import { ColumnDefinition } from '../../utils/exportToCsv'
import { formatNorwegianDate } from '../../utils/dateUtils'
import { localizeApiDetail } from '../../i18n/apiError'
import { useLatest } from '../Bruktbil/useLatest'
import type {
  AkseldriftMerker, AkseldriftModeller, AkseldriftQuery, AkseldriftSegmenter, Drive, DriftColumns, Periods, SegmentRow,
} from './types'

type Row = DriftColumns & { brand?: string; model?: string; master_model_id?: number; segment?: string; segment_id?: number }
type SortKey = 'brand' | 'model' | 'segment' | keyof DriftColumns
type Sort = { key: SortKey; direction: 'asc' | 'desc' } | null

// Vertical line between column groups, same as Nybilstatistikk
const DIVIDER = 'border-r-2 border-gray-300 dark:border-gray-600'

const int = (n: number) => n.toLocaleString('nb-NO')
const pct = (n: number | null) =>
  n === null ? '–' : `${n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
const signed = (n: number | null) =>
  n === null ? '–' : `${n > 0 ? '+' : ''}${n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`
const changeColor = (n: number | null) =>
  n === null ? 'text-gray-400' : n > 0 ? 'text-green-600 dark:text-green-400' : n < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'

/**
 * Teknisk › Akseldrift › Merke, › Modell and › Segment: one row per brand,
 * model or segment with 2WD/4WD counts and shares and the change vs last year.
 * Same grid as Alder's Merke table: sortable headers, a pinned TOTAL row,
 * search and export. A click on a segment opens its models (#911): the Modell
 * table with `segment` set.
 */
export default function AkseldriftTable({ kind, queryKey, sortDrive, onPeriods, segment, onSelectSegment, onBack }: {
  kind: 'merke' | 'modell' | 'segment'
  queryKey: string
  /** Set by «Se alle» on Oversikt: start sorted on this akseldrift */
  sortDrive: Drive | null
  /** Reports the periods of the latest answer, for the explanation box */
  onPeriods: (periods: Periods) => void
  /** Modell only: limit the models to this segment */
  segment?: SegmentRow
  /** Segment only: a segment was clicked */
  onSelectSegment?: (segment: SegmentRow) => void
  /** With `segment`: back to all segments */
  onBack?: () => void
}) {
  const { t } = useTranslation('teknisk')
  const [response, setResponse] = useState<AkseldriftMerker | AkseldriftModeller | AkseldriftSegmenter | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [kunBegge, setKunBegge] = useState(false)
  const [sort, setSort] = useState<Sort>(sortDrive ? { key: `count_${sortDrive}`, direction: 'desc' } : null)
  const isModel = kind === 'modell'
  const isSegment = kind === 'segment'
  const segmentId = segment?.segment_id

  useLatest(alive => {
    const params: AkseldriftQuery = JSON.parse(queryKey)
    setLoading(true)
    setError(null)
    const request = isModel
      ? publicAPI.getAkseldriftModeller({
        ...params,
        ...(kunBegge ? { kun_begge: true } : {}),
        ...(segmentId !== undefined ? { segment_id: segmentId } : {}),
      })
      : isSegment
        ? publicAPI.getAkseldriftSegmenter(params)
        : publicAPI.getAkseldriftMerker(params)
    request
      .then(res => {
        if (!alive()) return
        setResponse(res.data)
        onPeriods(res.data.periods)
      })
      .catch(err => {
        if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('akseldrift.loadError'))
      })
      .finally(() => { if (alive()) setLoading(false) })
  }, [queryKey, kind, kunBegge, segmentId])

  const rows = useMemo(() => {
    const data: Row[] = response?.data ?? []
    const needle = search.trim().toLowerCase()
    const filtered = needle
      ? data.filter(r => `${r.brand ?? ''} ${r.model ?? ''} ${r.segment ?? ''}`.toLowerCase().includes(needle))
      : data
    if (!sort) return filtered
    const dir = sort.direction === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      if (sort.key === 'brand' || sort.key === 'model' || sort.key === 'segment') {
        return dir * String(a[sort.key] ?? '').localeCompare(String(b[sort.key] ?? ''), 'nb')
      }
      // Rows without a value (no comparison) always go last
      const av = a[sort.key]
      const bv = b[sort.key]
      if (av === null) return bv === null ? 0 : 1
      if (bv === null) return -1
      return dir * (av - bv)
    })
  }, [response, search, sort])

  const handleSort = (key: SortKey) => {
    if (sort?.key === key) setSort(sort.direction === 'asc' ? { key, direction: 'desc' } : null)
    else setSort({ key, direction: 'asc' })
  }

  const sortIcon = (key: SortKey) =>
    sort?.key === key
      ? sort.direction === 'asc' ? <ArrowUp className="w-4 h-4 inline ml-1" /> : <ArrowDown className="w-4 h-4 inline ml-1" />
      : <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />

  // `divider` closes a column group with a vertical line, as on Nybilstatistikk
  const columns: { key: SortKey; label: string; render: (r: DriftColumns) => React.ReactNode; divider?: boolean }[] = [
    { key: 'total', label: t('akseldrift.table.total'), render: r => int(r.total), divider: true },
    { key: 'count_2wd', label: t('akseldrift.table.count2wd'), render: r => int(r.count_2wd) },
    { key: 'count_4wd', label: t('akseldrift.table.count4wd'), render: r => int(r.count_4wd), divider: true },
    { key: 'share_2wd', label: t('akseldrift.table.share2wd'), render: r => pct(r.share_2wd) },
    { key: 'share_4wd', label: t('akseldrift.table.share4wd'), render: r => pct(r.share_4wd), divider: true },
    {
      key: 'share_4wd_change_pp', label: t('akseldrift.table.share4wdChange'),
      render: r => <span className={changeColor(r.share_4wd_change_pp)}>{signed(r.share_4wd_change_pp)}</span>,
    },
    {
      key: 'total_change_pct', label: t('akseldrift.table.totalChange'),
      render: r => <span className={changeColor(r.total_change_pct)}>{signed(r.total_change_pct)}</span>,
    },
  ]

  const headerLabel = (key: SortKey) =>
    key === 'brand' ? t('akseldrift.colBrand') : key === 'model' ? t('akseldrift.colModel')
      : key === 'segment' ? t('akseldrift.colSegment')
        : columns.find(c => c.key === key)?.label ?? ''

  // The name columns before the numbers: Segment, or Merke (+ Modell)
  const nameKeys: SortKey[] = isSegment ? ['segment'] : isModel ? ['brand', 'model'] : ['brand']

  const query: AkseldriftQuery = JSON.parse(queryKey)
  const exportColumns: ColumnDefinition[] = [
    { key: 'rank', label: '#' },
    ...nameKeys.map(key => ({ key, label: headerLabel(key) })),
    { key: 'total', label: t('akseldrift.table.total') },
    { key: 'count_2wd', label: t('akseldrift.table.count2wd') },
    { key: 'count_4wd', label: t('akseldrift.table.count4wd') },
    { key: 'share_2wd', label: t('akseldrift.table.share2wd'), format: v => pct(v) },
    { key: 'share_4wd', label: t('akseldrift.table.share4wd'), format: v => pct(v) },
    { key: 'share_4wd_change_pp', label: t('akseldrift.table.share4wdChange'), format: v => signed(v) },
    { key: 'total_change_pct', label: t('akseldrift.table.totalChange'), format: v => signed(v) },
  ]

  const title = segment
    ? t('akseldrift.table.titleModelsInSegment', { segment: segment.segment })
    : isModel ? t('akseldrift.table.titleModels')
      : isSegment ? t('akseldrift.table.titleSegments') : t('akseldrift.table.titleBrands')
  const summary = response?.summary

  return (
    <div className="card p-6" data-testid={segment ? 'akseldrift-table-segment-modell' : `akseldrift-table-${kind}`}>
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline mb-3">
          <ArrowLeft className="w-4 h-4" />
          {t('akseldrift.table.backToSegments')}
        </button>
      )}
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {title}
          {sort && (
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
              {t('akseldrift.table.sortedBy', { column: headerLabel(sort.key) })}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          {sort && (
            <button onClick={() => setSort(null)} className="btn-secondary text-sm">
              {t('akseldrift.table.resetSort')}
            </button>
          )}
          <ExportButtons
            data={rows.map((r, i) => ({ rank: i + 1, ...r }))}
            columns={exportColumns}
            metadata={{
              title: `${t('title')} – ${t('tabs.akseldrift')} – ${title}`,
              dateRange: response
                ? `${formatNorwegianDate(response.periods.current.start_date)} – ${formatNorwegianDate(response.periods.current.end_date)}`
                : '',
              filters: [
                query.varebil === true ? t('akseldrift.table.filterVarebil') : query.varebil === false ? t('akseldrift.table.filterPersonbil') : '',
                query.mine_postnummer ? t('akseldrift.table.filterPostnummer', { value: query.mine_postnummer }) : '',
                segment ? t('akseldrift.table.filterSegment', { value: segment.segment }) : '',
                isModel && kunBegge ? t('akseldrift.table.onlyBoth') : '',
              ].filter(Boolean),
            }}
            disabled={loading}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex-1 min-w-[240px]">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder={
              isModel ? t('akseldrift.table.searchModels')
                : isSegment ? t('akseldrift.table.searchSegments') : t('akseldrift.table.searchBrands')
            }
          />
        </div>
        {isModel && (
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={kunBegge}
              onChange={e => setKunBegge(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            {t('akseldrift.table.onlyBoth')}
            {response && 'both_count' in response && (
              <span className="text-gray-500 dark:text-gray-400" data-testid="akseldrift-both-count">
                {t('akseldrift.table.bothCount', { count: response.both_count })}
              </span>
            )}
          </label>
        )}
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table data-table-compact">
            <thead>
              <tr>
                <th className="text-center w-12">#</th>
                {nameKeys.map(key => (
                  <th key={key} className="text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort(key)}>
                    {headerLabel(key)} {sortIcon(key)}
                  </th>
                ))}
                {columns.map(c => (
                  <th key={c.key} className={`text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 ${c.divider ? DIVIDER : ''}`} onClick={() => handleSort(c.key)}>
                    {c.label} {sortIcon(c.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {summary && (
                <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
                  <td className="text-center py-2"><span className="text-primary-600 dark:text-primary-400">&#8721;</span></td>
                  <td className="font-bold text-primary-700 dark:text-primary-300 py-2" colSpan={nameKeys.length}>TOTAL</td>
                  {columns.map(c => <td key={c.key} className={`text-right py-2 ${c.divider ? DIVIDER : ''}`}>{c.render(summary)}</td>)}
                </tr>
              )}
              {rows.map((r, i) => (
                <tr key={isModel ? r.master_model_id : isSegment ? r.segment_id : r.brand}>
                  <td className="text-center text-gray-500 dark:text-gray-400">{i + 1}</td>
                  {isSegment ? (
                    <td>
                      <button
                        onClick={() => onSelectSegment?.(r as SegmentRow)}
                        className="flex items-center gap-1 whitespace-nowrap font-medium text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {r.segment}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </td>
                  ) : (
                    <>
                      <td>{r.brand}</td>
                      {isModel && <td>{r.model}</td>}
                    </>
                  )}
                  {columns.map(c => <td key={c.key} className={`text-right ${c.divider ? DIVIDER : ''}`}>{c.render(r)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">{t('akseldrift.noData')}</p>
          )}
        </div>
      )}
    </div>
  )
}
